// @flow

import moment from 'moment'
import { createActions, handleActions } from 'redux-actions'
import { call, put, select, takeEvery } from 'redux-saga/effects'
import userReservationResource from 'frontend_core/dist/rest/resources/userReservation'
import masterResource from 'frontend_core/dist/rest/resources/master'
import SMConst from 'frontend_core/dist/constants/sm-const'
import {
  getIsHourly,
  createReservedSchedules,
} from 'frontend_core/dist/helpers/reservation'
import type { Master } from 'types/v1/master'
import { type Option, type ReservationType } from 'types/v1/reservation'
import { SET_UI_STATE } from './ui'
import {
  actions as detailActions,
  type setReservationDetailAction,
  type State as ReservationDetailState,
} from './reservationDetail'

/**
 ************************************************************
 * 初期ステート
 ************************************************************
 **/

export type State = {
  plan: ?Object,
  startedAt: ?moment,
  endedAt: ?moment,
  isHourly: boolean,
  startHour: ?number,
  endHour: ?number,
  optionItems: Array<Option>,
  hasChanged: boolean,
  // ドライラン後の予約保存
  unapprovedReservation: ReservationType,
  // 成功フラグ/失敗時の情報
  successEditReservation: boolean,
  failedEditReservation: ?Error,
}

const initialState: State = {
  plan: {},
  startedAt: null,
  endedAt: null,
  isHourly: true,
  startHour: null,
  endHour: null,
  optionItems: [],
  hasChanged: false,
  // ドライラン後の予約保存
  unapprovedReservation: {},
  // 成功フラグ/失敗時の情報
  successEditReservation: false,
  failedEditReservation: null,
}

/**
 ************************************************************
 * ActionTypes
 ************************************************************
 **/

const actionTypes = {
  EDIT_RESERVATION_DRYRUN: 'EDIT_RESERVATION_DRYRUN',
  SUCCESS_EDIT_RESERVATION_DRYRUN: 'SUCCESS_EDIT_RESERVATION_DRYRUN',
  FAILED_EDIT_RESERVATION_DRYRUN: 'FAILED_EDIT_RESERVATION_DRYRUN',
  EDIT_RESERVATION: 'EDIT_RESERVATION',
  SUCCESS_EDIT_RESERVATION: 'SUCCESS_EDIT_RESERVATION',
  FAILED_EDIT_RESERVATION: 'FAILED_EDIT_RESERVATION',
}

/**
 ************************************************************
 * Actions
 ************************************************************
 **/

export const actions = createActions(
  actionTypes.EDIT_RESERVATION_DRYRUN,
  actionTypes.SUCCESS_EDIT_RESERVATION_DRYRUN,
  actionTypes.FAILED_EDIT_RESERVATION_DRYRUN,
  actionTypes.EDIT_RESERVATION,
  actionTypes.SUCCESS_EDIT_RESERVATION,
  actionTypes.FAILED_EDIT_RESERVATION,
)

/*
 ************************************************************
 * reducer
 ************************************************************
 */

export const reducer = handleActions(
  {
    [detailActions.setReservationDetail]: (
      state: State,
      action: setReservationDetailAction,
    ) => {
      const reservation = action.payload.detail
      const isRentTypeStay = reservation.rent_type === SMConst.RentType.Stay
      const plan = reservation.plan || {}
      const startedAt = moment(
        isRentTypeStay ? reservation.stay_checkin_at : reservation.started_at,
      ).utc()
      const endedAt = moment(
        isRentTypeStay ? reservation.stay_checkout_at : reservation.ended_at,
      ).utc()
      const isHourly = getIsHourly(plan, reservation.is_hourly)
      if (!isRentTypeStay && !isHourly) {
        // 1日価格の場合にendedAtが翌日になっているため調整する
        endedAt.add(-1, 'days')
      }
      return {
        ...state,
        plan,
        startedAt: moment(startedAt)
          .hour(0)
          .minute(0)
          .second(0),
        endedAt: moment(endedAt)
          .hour(0)
          .minute(0)
          .second(0),
        isHourly,
        startHour: startedAt.hours(),
        endHour: endedAt.hours(),
        optionItems: reservation.reserved_option_items || [],
        unapprovedReservation: reservation,
      }
    },
    [actions.editReservationDryrun]: (state: State, action: Object) => {
      return {
        ...state,
        plan: action.payload.plan || state.plan,
        startedAt: action.payload.startedAt || state.startedAt,
        endedAt: action.payload.endedAt || state.endedAt,
        isHourly:
          action.payload.plan !== undefined
            ? getIsHourly(
                action.payload.plan,
                action.payload.isHourly || state.isHourly,
              )
            : action.payload.isHourly !== undefined
              ? action.payload.isHourly
              : state.isHourly,
        startHour:
          action.payload.startHour !== undefined
            ? action.payload.startHour
            : state.startHour,
        endHour:
          action.payload.endHour !== undefined
            ? action.payload.endHour
            : state.endHour,
        optionItems: action.payload.optionItems || state.optionItems,
        failedEditReservation: null,
        hasChanged:
          action.payload.plan ||
          action.payload.startedAt ||
          action.payload.endedAt ||
          action.payload.isHourly ||
          action.payload.startHour ||
          action.payload.endHour ||
          action.payload.optionItems,
      }
    },
    [actions.successEditReservationDryrun]: (state: State, action: Object) => ({
      ...state,
      unapprovedReservation: action.payload,
    }),
    [actions.failedEditReservationDryrun]: (state: State, action: Object) => ({
      ...state,
      failedEditReservation: action.payload,
    }),
    [actions.editReservation]: (state: State, action: Object) => ({
      ...state,
      failedEditReservation: null,
    }),
    [actions.successEditReservation]: (state: State, action: Object) => ({
      ...state,
      successEditReservation: true,
    }),
    [actions.failedEditReservation]: (state: State, action: Object) => ({
      ...state,
      failedEditReservation: action.payload,
    }),
  },
  initialState,
)

/*
 ************************************************************
 * Utility Methods
 ************************************************************
 */

type UpdateReservationParams = {
  userId: string,
  spaceId: string,
  roomId: string,
  isHourly: boolean,
  plan: Object,
  startedAt: moment,
  endedAt: moment,
  startHour?: number,
  endHour?: number,
  billing: {
    id: string,
  },
  options: Array<{
    id: string,
    count: number,
  }>,
}

function createUpdateParams(
  userId: string,
  reservationDetail: ReservationDetailState,
  reservationEdit: State,
  paidyPaymentId?: ?string = null,
  paidyChargeAmount?: ?number = null,
): ?Object {
  const reservation = reservationDetail.detail || {}
  const reservationRoom = reservation.room || {}

  const spaceId = (reservationRoom.space || {}).id
  const roomId = reservationRoom.id

  // バリデーション
  if (
    !spaceId ||
    !roomId ||
    !reservationEdit.plan ||
    !reservationEdit.plan.id
  ) {
    return null
  }

  let paidyParams = {}
  if (paidyPaymentId && paidyChargeAmount) {
    paidyParams = {
      paidy_payment_id: paidyPaymentId,
      paidy_charge_amount: paidyChargeAmount,
    }
  }
  return {
    user_id: userId,
    space_id: spaceId,
    room_id: roomId,
    plan_id: reservationEdit.plan.id,
    is_hourly: reservationEdit.isHourly,
    reserved_schedules: createReservedSchedules(
      reservationEdit.isHourly,
      reservationEdit.plan,
      moment(reservationEdit.startedAt).hour(reservationEdit.startHour || 0),
      moment(reservationEdit.endedAt).hour(reservationEdit.endHour || 0),
    ),
    is_changed_price: 0,
    billing: {
      id: (reservation.billing || {}).id,
    },
    reserved_option_items: (reservationEdit.optionItems || []).map(o => ({
      option_item_id: o.option_item_id,
      number: o.number || 1,
    })),
    ...paidyParams,
  }
}

/*
 ************************************************************
 * API連携 非同期アクション
 ************************************************************
 */
function* editReservationDryrun({
  payload,
}: {
  payload: {
    userId: string,
  },
}) {
  const { userId } = payload

  const { reservationDetail, reservationEdit } = yield select(state => ({
    reservationDetail: state.reservationDetail,
    reservationEdit: state.reservationEdit,
  }))
  const reservation = reservationDetail.detail || {}

  const updateParams = createUpdateParams(
    userId,
    reservationDetail,
    reservationEdit,
  )
  if (!updateParams) {
    return
  }

  yield put({
    type: SET_UI_STATE,
    payload: {
      isLoading: true,
    },
  })

  try {
    const canceledReservation = yield call(
      userReservationResource.update,
      userId,
      reservation.id,
      updateParams,
      {
        dryrun: true,
      },
    )
    yield put({
      type: actionTypes.SUCCESS_EDIT_RESERVATION_DRYRUN,
      payload: canceledReservation,
    })
  } catch (e) {
    yield put({
      type: actionTypes.FAILED_EDIT_RESERVATION_DRYRUN,
      payload: e,
    })
  } finally {
    yield put({
      type: SET_UI_STATE,
      payload: {
        isLoading: false,
      },
    })
  }
}

function* editReservation({
  payload,
}: {
  payload: {
    userId: string,
    paidyPaymentId?: ?string,
    paidyChargeAmount?: ?number,
  },
}) {
  const { userId, paidyPaymentId, paidyChargeAmount } = payload

  const { reservationDetail, reservationEdit } = yield select(state => ({
    reservationDetail: state.reservationDetail,
    reservationEdit: state.reservationEdit,
  }))
  const reservation = reservationDetail.detail || {}

  const updateParams = createUpdateParams(
    userId,
    reservationDetail,
    reservationEdit,
    paidyPaymentId,
    paidyChargeAmount,
  )
  if (!updateParams) {
    return
  }

  yield put({
    type: SET_UI_STATE,
    payload: {
      isLoading: true,
    },
  })

  try {
    const canceledReservation = yield call(
      userReservationResource.update,
      userId,
      reservation.id,
      updateParams,
    )
    yield put({
      type: actionTypes.SUCCESS_EDIT_RESERVATION,
      payload: canceledReservation,
    })
  } catch (e) {
    yield put({
      type: actionTypes.FAILED_EDIT_RESERVATION,
      payload: e,
    })
  } finally {
    yield put({
      type: SET_UI_STATE,
      payload: {
        isLoading: false,
        isPaidyChargeActive: false,
      },
    })
  }
}

/**
 ************************************************************
 * saga
 ************************************************************
 **/

export const reservationEditSagas = [
  takeEvery(actionTypes.EDIT_RESERVATION_DRYRUN, editReservationDryrun),
  takeEvery(actionTypes.EDIT_RESERVATION, editReservation),
]



// WEBPACK FOOTER //
// ./src/redux/modules/reservationEdit.js